import { LBBCompany, LBBContact, LBBOutboundContact, MinimalUser, User } from '.';

export interface Visitor {
  created: number;
  updated: number;
  sid: string;
  orgId: number;
  sbid: string;
  replacedSbid: string; // identify id swap
  location: string;
  url: string;
  urlTitle: string;
  userAgent?: string;
  routedUserIds: number[];
  helpStatus: VisitorHelpStatus;
  helpStatusUpdated: number;
  helpedBy: number; // admin user id
  helpedBySid: string; // admin socket id
  helpedByAvatar: string;
  helpedByName: string;
  helpedByTitle: string;
  helpedByConnectionType: null | 'webrtc' | 'phone';
  isSpying: boolean;
  isTakeover: boolean;
  isTakeoverAccepted: boolean;
  isSpyingAccepted: boolean;
  isSpyingRejected: boolean;
  isVideo: boolean;
  isVideoAccepted: boolean;
  isDisplayshare: boolean;
  isDisplayshareAccepted: boolean;
  isCallWaitingOnVisitor: boolean;
  isDisplaydemo: boolean;
  isRecording: boolean;
  callConversationId: number;
  isRecordingAccepted: boolean;
  isRecordingRejected: boolean;
  wantsChat: boolean;
  isPageVisible: boolean;
  isVisitorVideoAllowed: boolean;
  isVisitorRequestingVideo: boolean;
  hasAlerted: boolean;
  hasDialed: boolean;
  displayName: string;
  email: string;
  title: string;
  customId?: string;
  customMetadata?: Record<string, unknown>;
  lastSeen?: number;
  callStartTime?: number;
  blocks?: VisitorBlock[];
  streams: number[];
  commitHash?: string;
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  utmTerm?: string;
  clearbitRevealData?: any;
  sixsenseRevealData?: any;
}

export type PartialVisitor = Partial<Visitor> & { sbid: Visitor['sbid'] };

export type WidgetVisitor = {
  helpedBy: number;
  helpedBySid: string;
  helpedByName: string;
  helpedByTitle: string;
  helpedByAvatar: string;
  helpStatus: VisitorHelpStatus;
  isSpying: boolean;
  isTakeover: boolean;
  isDisplayshare: boolean;
  isVideo: boolean;
  orgId: number;
  customId: string;
  url: string;
  urlTitle: string;
  isPageVisible: boolean;
  isVisitorVideoAllowed: boolean;
  isVisitorRequestingVideo: boolean;
  isRecordingAccepted: boolean;
  isRecordingRejected: boolean;
  isProactiveCallWithoutAgent: boolean;
  wantsChat: boolean;
  callStartTime?: number;
  callConversationId: number;
  scrollYPercent: number;
  timeOnPage: number;
};

export type VisitorUpdate = {
  type: 'REMOVE' | 'UPDATE';
  data: PartialVisitor;
};

export type VisitorUpdates = Record<string, VisitorUpdate>;

export type VisitorInteractionUpdate = Partial<
  Pick<
    Visitor,
    | 'sbid'
    | 'isTakeover'
    | 'isVideo'
    | 'isDisplayshare'
    | 'isDisplaydemo'
    | 'isVisitorVideoAllowed'
    | 'isRecording'
    | 'callConversationId'
    | 'isVideoAccepted'
  >
>;
export type VisitorInteractionUpdateErrorCode = 'UNKNOWN' | 'NOT_ASSOCIATED';

export enum VisitorHelpStatus {
  IDLE = 'IDLE',
  WAITING = 'WAITING',
  ALERT = 'ALERT',
  ACTIVE = 'ACTIVE',
  FINISHED = 'FINISHED',
  MISSED = 'MISSED',
}
export enum BLOCKED_VISITOR_STATUS {
  ACTIVE = 'ACTIVE',
  INACTIVE = 'INACTIVE',
}
export enum BLOCKED_VISITOR_TYPE {
  VISITOR = 'VISITOR',
  IP_ADDRESS = 'IP_ADDRESS',
}
export enum BLOCKED_VISITOR_ACTION {
  BLOCK = 'BLOCK',
  SILENCE = 'SILENCE',
}

export interface UniqueVisitor {
  id: string;
  customId?: Visitor['customId'];
  customMetadata?: Visitor['customMetadata'];
  lastSeen?: number;
  lastLocation?: string;
  displayName?: string;
  contact?: LBBContact;
  utmCampaign?: string;
  utmContent?: string;
  utmMedium?: string;
  utmSource?: string;
  utmTerm?: string;
  company?: LBBCompany;
}

export type BlockedVisitor = {
  id: string;
  ipAddress: string;
  note?: string;
  status: BLOCKED_VISITOR_STATUS;
  blockType: BLOCKED_VISITOR_TYPE;
  action: BLOCKED_VISITOR_ACTION;
  applied: number;
  dateCreated: number;
  dateUpdated: number;
  uniqueVisitor: UniqueVisitor;
  createdBy: User;
};

export type VisitorBlock = {
  id: string;
  note?: string;
  status?: BLOCKED_VISITOR_STATUS;
  blockType?: BLOCKED_VISITOR_TYPE;
  action?: BLOCKED_VISITOR_ACTION;
};

export type VisitorCustomMetadata = {
  email: string;
  displayName?: string;
  avatar?: string;
} & Record<string, string | number>;

export type VisitorIdentity = {
  customId: string | number | undefined;
  customMetadata: VisitorCustomMetadata;
  createHubspotContact: boolean;
  secureIdentityHash?: string;
};

export type VisitorTopPage = {
  url: string;
  visits: number;
};

export type VisitorActivityResponse = {
  page: number;
  nextPage: number | null;
  total: number;
  pageSize: number;
  items: VisitorActivity[];
  filters: string | null;
  search: string | null;
  sort: string | null;
};

export type ChatDialogue = {
  id: number;
  visitor: UniqueVisitor;
  conversationId: number;
  dateStarted: string;
  participantUserIds: number[];
  availableUserIds: number[];
  hasAnswer: boolean;
};

export enum VisitorActivityType {
  // VisitorAction "actions"
  REQUESTED_HELP = 'REQUESTED_HELP',
  HELP_REQUEST_MISSED = 'HELP_REQUEST_MISSED',
  REJECTED_CALL = 'REJECTED_CALL',
  ALERT = 'ALERT',
  PAGE_VISIT = 'PAGE_VISIT',
  FEEDBACK = 'FEEDBACK',
  // VisitorInteraction "kinds"
  SPYING = 'SPYING',
  VIDEO = 'VIDEO',
  TAKEOVER = 'TAKEOVER',
  DISPLAYSHARE = 'DISPLAYSHARE',
  DISPLAYDEMO = 'DISPLAYDEMO',
  // VisitorSession
  SESSION = 'SESSION',
  CALL = 'CALL',
}

export type VisitorActivityAgent = {
  id: number;
  displayName: string;
  avatar: { imageUrl: string } | null;
};

export type VisitorActivity = {
  id: string;
  type: VisitorActivityType;
  sessionId: string;
  data: {
    url?: string;
  } | null;
  startTime: number;
  lastSeenTime: number | null;
  acceptTime: number | null;
  url: string;
  userId: number | null;
  user: VisitorActivityAgent | null;
  referrer: string | null;
  contact?: LBBOutboundContact;
  visitor?: UniqueVisitor;
  visitorId?: string;
  visitorDisplayName?: string;
  availableUserIds?: number[];
};

export enum SESSION_INTERACTION_KIND {
  SPYING = 'SPYING',
  VIDEO = 'VIDEO',
  TAKEOVER = 'TAKEOVER',
}

export type SessionInteraction = {
  id: number;
  sessionId: number;
  kind: SESSION_INTERACTION_KIND;
  user: MinimalUser;
  startTime: number;
  lastSeen: number;
};

export enum SESSION_ACTION {
  REQUESTED_HELP = 'REQUESTED_HELP',
  PAGE_VISIT = 'PAGE_VISIT',
}

export type SessionAction = {
  id: number;
  sessionId: number;
  action: SESSION_ACTION;
  timestamp: number;
  data?: { url: string; urlTitle: string };
};

export type VisitorSession = {
  id: string;
  uniqueVisitorId: string;
  ipAddress: string;
  location?: string;
  referrer: string;
  userAgent?: string;
  startTime: number;
  endTime: number;
  sessionInteractions: SessionInteraction[];
  sessionActions: SessionAction[];
  isFirst: boolean;
};
